import { memo, useState, useCallback, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { AudioLines, X, Loader2, CheckCircle, Play, Pause, Download, Eye, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { downloadFile } from '../../utils/downloadFile';
import { useWorkflowStore } from '../../store/workflowStore';
import { useNodeExecution } from '../../hooks/useNodeExecution';
import { useWorkflowTrigger } from '../../hooks/useWorkflowTrigger';
import NodeError from './NodeError';
import NodeResetButton from './NodeResetButton';
import {
  ReconfigureButton, ReconfigureNotice, useReconfigure,
} from './ReconfigureControls';
import { useMediaPreview } from '../../hooks/useMediaPreview';
import SendToTimelineButton from '../SendToTimelineButton';
import DirectorsTrigger from '../studio/DirectorsTrigger';
import PromptField from '../studio/PromptField';
import VoicePreviewButton from './VoicePreviewButton';
import NodeRequirements from './NodeRequirements';
import { missingOf, requirementsTitle, textNeed, need } from '../../utils/nodeRequirements';

// Voces que acepta elevenlabs/v2-multilingual en Replicate (lista exacta
// provista desde el desplegable del modelo; no agregar nombres inventados).
const VOICES = [
  'Rachel', 'Drew', 'Clyde', 'Paul', 'Aria', 'Domi', 'Dave', 'Roger', 'Fin',
  'Sarah', 'James', 'Jane', 'Juniper', 'Arabella', 'Hope', 'Bradford',
  'Reginald', 'Gaming', 'Austin', 'Kuon', 'Blondie', 'Priyanka', 'Alexandra',
  'Monika', 'Mark', 'Grimblewood',
];

// 29 idiomas soportados por ElevenLabs Multilingual v2
const LANGUAGES = [
  { id: 'es', label: 'Español' },
  { id: 'en', label: 'English' },
  { id: 'ja', label: 'Japonés' },
  { id: 'zh', label: 'Chino mandarín' },
  { id: 'de', label: 'Alemán' },
  { id: 'hi', label: 'Hindi' },
  { id: 'fr', label: 'Francés' },
  { id: 'ko', label: 'Coreano' },
  { id: 'pt', label: 'Portugués' },
  { id: 'it', label: 'Italiano' },
  { id: 'id', label: 'Indonesio' },
  { id: 'nl', label: 'Neerlandés' },
  { id: 'tr', label: 'Turco' },
  { id: 'fil', label: 'Filipino' },
  { id: 'pl', label: 'Polaco' },
  { id: 'sv', label: 'Sueco' },
  { id: 'bg', label: 'Búlgaro' },
  { id: 'ro', label: 'Rumano' },
  { id: 'ar', label: 'Árabe' },
  { id: 'cs', label: 'Checo' },
  { id: 'el', label: 'Griego' },
  { id: 'fi', label: 'Finlandés' },
  { id: 'hr', label: 'Croata' },
  { id: 'ms', label: 'Malayo' },
  { id: 'sk', label: 'Eslovaco' },
  { id: 'da', label: 'Danés' },
  { id: 'ta', label: 'Tamil' },
  { id: 'uk', label: 'Ucraniano' },
  { id: 'ru', label: 'Ruso' },
];

const SliderField = ({ label, value, onChange, min, max, step, testId }) => (
  <div className="eleV2-slider-row">
    <span className="nano-option-label">{label}</span>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      className="videoSound-slider"
      data-testid={testId}
    />
    <span className="videoSound-duration-value">{value}</span>
  </div>
);

const EleV2Node = memo(({ id, data, selected }) => {
  const [chronometer, setChronometer] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  // Compatibilidad: nodos creados por MCP antes del fix guardaban el texto
  // en data.prompt. data.text tiene prioridad (lo último editado en la UI).
  const [text, setText] = useState(data.text || data.prompt || '');
  const [voice, setVoice] = useState(data.voice || 'Rachel');
  const [languageCode, setLanguageCode] = useState(data.languageCode || 'es');
  const [stability, setStability] = useState(data.stability ?? 0.5);
  const [similarityBoost, setSimilarityBoost] = useState(data.similarityBoost ?? 0.75);
  const [style, setStyle] = useState(data.style ?? 0);
  const [speed, setSpeed] = useState(data.speed ?? 1);
  const [previousText, setPreviousText] = useState(data.previousText || '');
  const [nextText, setNextText] = useState(data.nextText || '');
  const [showContext, setShowContext] = useState(!!(data.previousText || data.nextText));
  const [lastText, setLastText] = useState(data.lastText || '');

  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const addAsset = useWorkflowStore((state) => state.addAsset);

  const { processNode, status: execStatus, error: execError, reset: resetExec, insufficientCredits } = useNodeExecution();
  const { openAudioPreview } = useMediaPreview();

  const isProcessing = execStatus === 'uploading' || execStatus === 'queued' || execStatus === 'processing';

  useEffect(() => {
    updateNodeData(id, {
      text, voice, languageCode, stability, similarityBoost, style, speed,
      previousText, nextText,
    });
  }, [text, voice, languageCode, stability, similarityBoost, style, speed, previousText, nextText]); // eslint-disable-line

  useEffect(() => {
    let interval;
    if (isProcessing) {
      setChronometer(0);
      interval = setInterval(() => setChronometer((prev) => prev + 1), 1000);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [isProcessing]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const togglePlay = useCallback(() => {
    const audio = document.getElementById(`elev2-audio-${id}`);
    if (!audio) return;
    if (isPlaying) audio.pause(); else audio.play();
    setIsPlaying(!isPlaying);
  }, [id, isPlaying]);

  // Regenerar descarta el resultado; volver a configurar lo conserva.
  const { reconfigure, setReconfigure, showForm } = useReconfigure(data);

  const handleProcess = useCallback(async () => {
    const usedText = text.trim();
    if (!usedText) return false;
    resetExec();

    const params = {
      prompt: usedText,
      voice,
      language_code: languageCode,
      stability,
      similarity_boost: similarityBoost,
      style,
      speed,
    };
    if (previousText.trim()) params.previous_text = previousText.trim();
    if (nextText.trim()) params.next_text = nextText.trim();

    const result = await processNode({
      resultType: 'elevenlabs_v2',
      files: [],
      params,
    });

    if (result?.outputUrl) {
      setLastText(usedText);
      setReconfigure(false);
      updateNodeData(id, {
        hasOutput: true,
        outputUrl: result.outputUrl,
        assetId: result.assetId ?? null,
        outputType: 'audio',
        lastText: usedText,
        processedAt: new Date().toISOString(),
      });
      addAsset({
        id: `asset_${Date.now()}`,
        type: 'audio',
        tool: 'eleV2',
        filename: `elev2_${Date.now()}.mp3`,
        url: result.outputUrl,
        prompt: usedText,
        createdAt: new Date().toISOString(),
      });
      return true;
    }
    return false;
  }, [id, text, voice, languageCode, stability, similarityBoost, style, speed,
      previousText, nextText, updateNodeData, addAsset, processNode, resetExec, setReconfigure]);

  const handleRegenerate = useCallback(() => {
    updateNodeData(id, { hasOutput: false, outputUrl: null });
    resetExec();
  }, [id, updateNodeData, resetExec]);

  const { isInQueue, isMyTurn } = useWorkflowTrigger(id, handleProcess);

  // Una voz guardada que el modelo ya no soporta (p. ej. Antoni/Elli de la
  // v1) NO se ejecuta en silencio con otra: se pide elegir una válida.
  const voiceUnavailable = !!voice && !VOICES.includes(voice);
  const canProcess = text.trim().length > 0 && !voiceUnavailable;

  // NR-1 · qué le falta al nodo para generar (se muestra debajo del botón)
  const runMissing = missingOf([textNeed(text, 'escribir el texto a locutar'), need(!voiceUnavailable, 'elegir otra voz: la elegida ya no está disponible')]);

  return (
    <div className={`node-container eleV2-node ${selected ? 'selected' : ''} ${isInQueue ? 'workflow-queued' : ''} ${isMyTurn ? 'workflow-active' : ''}`} data-testid="elev2-node">
      <NodeResetButton nodeId={id} hasOutput={!!data.hasOutput} />

      {/* Header */}
      <div className="node-header eleV2-header">
        <div className="node-header-left">
          <AudioLines size={16} />
          <span>EleV2</span>
        </div>
        <button className="node-delete-btn" onClick={() => deleteNode(id)} data-testid="delete-elev2">
          <X size={14} />
        </button>
      </div>

      {/* Content */}
      <div className="node-content eleV2-content">
        {isProcessing ? (
          <div className="node-processing">
            <div className="node-chronometer">{formatTime(chronometer)}</div>
            <div className="node-processing-info">
              <Loader2 size={16} className="animate-spin" />
              <span>Generando voz...</span>
            </div>
            <p className="node-processing-hint">Voz: {voice} · {languageCode}</p>
          </div>
        ) : !showForm ? (
          /* ===== RESULT VIEW ===== */
          <div className="node-result qwen3tts-result">
            <audio
              id={`elev2-audio-${id}`}
              src={data.outputUrl}
              onEnded={() => setIsPlaying(false)}
            />
            <div className="qwen3tts-player">
              <button className="qwen3tts-play-btn" onClick={togglePlay} data-testid="elev2-play-btn">
                {isPlaying ? <Pause size={24} /> : <Play size={24} />}
              </button>
              <div className="qwen3tts-waveform">
                {[...Array(12)].map((_, i) => (
                  <div
                    key={`wave-${i}`}
                    className={`qwen3tts-wave-bar ${isPlaying ? 'animate' : ''}`}
                    style={{ animationDelay: `${i * 0.1}s` }}
                  />
                ))}
              </div>
            </div>
            <div className="node-result-actions">
              <button className="node-mini-preview-btn" onClick={() => openAudioPreview(data.outputUrl, 'EleV2 - Audio')} data-testid="elev2-preview-btn">
                <Eye size={12} /> Ver
              </button>
              <button className="node-mini-download-btn" onClick={(e) => { e.stopPropagation(); downloadFile(data.outputUrl, `elev2_${Date.now()}.mp3`); }} data-testid="elev2-download-btn">
                <Download size={12} /> Descargar
              </button>
              <SendToTimelineButton assetId={data.assetId} nodeId={id} url={data.outputUrl} kind="audio" label="eleV2" testIdPrefix="elev2-timeline" />
            </div>
            <div className="nano-regen-section">
              <span className="nano-section-label">Texto usado:</span>
              <PromptField
                value={text}
                onChange={setText}
                nodeId={id}
                nodeType="eleV2"
                label="Texto de regeneración"
                className="nano-prompt-textarea nano-regen-textarea"
                rows={2}
                testId="elev2-regen-text"
              />
              <button className="nano-regen-btn eleV2-regen-btn" onClick={handleRegenerate} data-testid="elev2-regen-btn">
                <RefreshCw size={12} />
                Regenerar
              </button>
              <ReconfigureButton onClick={() => setReconfigure(true)} testId="elev2-reconfigure-btn" />
            </div>
            <div className="node-result-badge">
              <CheckCircle size={12} />
              <span>Voz generada</span>
            </div>
          </div>
        ) : (
          <>
            {data.outputUrl && reconfigure && (
              <ReconfigureNotice
                previewUrl={null}
                onCancel={() => setReconfigure(false)}
                testId="elev2-reconfigure-notice"
              />
            )}

            {/* Texto */}
            <div className="nano-prompt-section">
              <div className="nano-prompt-header">
                <span className="nano-section-label">Texto a narrar *</span>
                <DirectorsTrigger nodeId={id} nodeType="eleV2" currentPrompt={text} onInsert={setText} />
              </div>
              <PromptField
                value={text}
                onChange={setText}
                nodeId={id}
                nodeType="eleV2"
                placeholder="Escribí o dictá el texto a convertir en voz (hasta ~10.000 caracteres)..."
                className="nano-prompt-textarea"
                rows={3}
                testId="elev2-text"
              />
            </div>

            {/* Voz + Idioma */}
            <div className="nano-options-row eleV2-options">
              <div className="nano-option">
                <span className="nano-option-label voice-preview-label">
                  Voz
                  <VoicePreviewButton voice={voice} testId="elev2-voice-preview" />
                </span>
                <select value={voiceUnavailable ? '' : voice} onChange={(e) => setVoice(e.target.value)} className={`nano-select ${voiceUnavailable ? 'invalid' : ''}`} data-testid="elev2-voice">
                  {voiceUnavailable && <option value="" disabled>Elegí una voz</option>}
                  {VOICES.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
                {voiceUnavailable && (
                  <span className="node-param-warning" data-testid="elev2-voice-unavailable">
                    Voz no disponible, elegí otra
                  </span>
                )}
              </div>
              <div className="nano-option">
                <span className="nano-option-label">Idioma</span>
                <select value={languageCode} onChange={(e) => setLanguageCode(e.target.value)} className="nano-select" data-testid="elev2-language">
                  {LANGUAGES.map((l) => (
                    <option key={l.id} value={l.id}>{l.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Sliders de control vocal */}
            <div className="eleV2-sliders">
              <SliderField label="Estabilidad" value={stability} onChange={setStability} min={0} max={1} step={0.05} testId="elev2-stability" />
              <SliderField label="Similitud" value={similarityBoost} onChange={setSimilarityBoost} min={0} max={1} step={0.05} testId="elev2-similarity" />
              <SliderField label="Estilo" value={style} onChange={setStyle} min={0} max={1} step={0.05} testId="elev2-style" />
              <SliderField label="Velocidad" value={speed} onChange={setSpeed} min={0.7} max={1.2} step={0.05} testId="elev2-speed" />
            </div>

            {/* Contexto (opcional) */}
            <button
              type="button"
              className="eleV2-context-toggle"
              onClick={() => setShowContext(!showContext)}
              data-testid="elev2-context-toggle"
            >
              {showContext ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              Contexto (texto anterior / siguiente) — opcional
            </button>
            {showContext && (
              <div className="eleV2-context-fields">
                <input
                  type="text"
                  className="nano-select"
                  value={previousText}
                  onChange={(e) => setPreviousText(e.target.value)}
                  placeholder="Texto anterior (continuidad emocional)"
                  data-testid="elev2-previous-text"
                />
                <input
                  type="text"
                  className="nano-select"
                  value={nextText}
                  onChange={(e) => setNextText(e.target.value)}
                  placeholder="Texto siguiente (continuidad emocional)"
                  data-testid="elev2-next-text"
                />
              </div>
            )}

            <button
              className="node-process-btn eleV2-process-btn"
              onClick={handleProcess}
              disabled={!canProcess}
              title={requirementsTitle(runMissing)}
              data-testid="elev2-process-btn"
            >
              <Play size={14} />
              {data.outputUrl && reconfigure ? 'Regenerar con cambios' : 'Generar voz'}
            </button>
            <NodeRequirements missing={runMissing} />
          </>
        )}

        <NodeError error={execError} insufficientCredits={insufficientCredits} />
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="node-handle output-handle audio-handle"
        isConnectable={!!data.hasOutput}
      />
    </div>
  );
});

EleV2Node.displayName = 'EleV2Node';

export default EleV2Node;
